import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  Banlist,
  BanlistArchetypeStatus,
  BanlistCardStatus,
  BanlistDiff,
  BanlistDiffRef,
  Nullable,
  UserWithToken,
  WithData,
  WithMeta,
} from '@ygoargentina/sdk/api/api.types';
import axiosClient from './request-client';
import { AxiosRequestConfig } from 'axios';

/**
 * Todo lo que hace falta para dibujar una lista.
 *
 * Como fragmento y no repetido en cada query: el editor guarda y vuelve a
 * pedir lo mismo, y una lista que se ve distinta según por dónde se pidió es
 * la clase de diferencia que aparece recién en pantalla.
 */
const BANLIST_FIELDS = gql`
  fragment BanlistFields on Banlist {
    id
    formatId
    name
    effectiveDate
    expirationDate
    isActive
    tags
    cardSort
    shareHash
    logoUrl
    previousBanlistId
    organizerId
    organizer {
      id
      name
      logoUrl
      slug
    }
    formats {
      id
      name
    }
    cards {
      id
      konamiId
      status
      previousStatus
      conditional
      card {
        konamiId
        name
        image
        frameType
      }
    }
    archetypes {
      id
      archetypeId
      archetype {
        id
        name
        type
      }
      status
      representativeKonamiId
      representativeCard {
        konamiId
        name
        image
      }
    }
    generics {
      id
      konamiId
      namePattern
      card {
        konamiId
        name
        image
      }
    }
    patterns {
      id
      namePattern
      status
      conditional
    }
  }
`;

export type BanlistFilters = {
  formatId?: number;
  isActive?: boolean;
  organizerId?: number;
  /** Sólo las listas sin dueño (las de Konami). */
  onlyGlobal?: boolean;
  page?: number;
  limit?: number;
};

export type BanlistCardInput = {
  konamiId: number;
  status: BanlistCardStatus;
  /** Sólo prohibida si el mazo usa un arquetipo de la lista de restricciones. Sin valor, el API guarda `false`. */
  conditional?: boolean;
};

export type BanlistArchetypeInput = {
  archetypeId: number;
  representativeKonamiId?: Nullable<number>;
  /** Nivel de restricción. Sin valor, el API guarda `FORBIDDEN`. */
  status?: BanlistArchetypeStatus;
};

/**
 * Una carta puntual o un patrón de nombre, nunca los dos. El API rechaza una
 * fila con los dos campos o con ninguno.
 */
export type BanlistGenericInput = {
  konamiId?: Nullable<number>;
  namePattern?: Nullable<string>;
};

export type BanlistCardPatternInput = {
  namePattern: string;
  status: BanlistCardStatus;
  /** Sin valor, el API guarda `false`. */
  conditional?: boolean;
};

export type CreateBanlistInput = {
  formatId: number;
  name: string;
  effectiveDate: Date | string;
  expirationDate?: Nullable<Date | string>;
  konamiWebId?: Nullable<number>;
  organizerId?: Nullable<number>;
  /**
   * Criterio de orden: 'NAME', 'TYPE' o null (orden de carga).
   *
   * También en el create: el editor manda el mismo input a las dos mutations,
   * y un campo que el input no declara hace que graphql-js rechace la mutation
   * entera antes del resolver.
   */
  cardSort?: Nullable<string>;
  /**
   * Lista a la que ésta sucede. Mismo formato; el dueño puede ser otro (una
   * lista de organizador que continúa la de Konami).
   */
  previousBanlistId?: Nullable<number>;
  cards?: BanlistCardInput[];
  archetypes?: BanlistArchetypeInput[];
  /** Reemplaza TODAS las genéricas. Omitir para no tocarlas. */
  generics?: BanlistGenericInput[];
  /** Reemplaza TODOS los patrones prohibidos. Omitir para no tocarlos. */
  patterns?: BanlistCardPatternInput[];
};

export type UpdateBanlistInput = {
  name?: string;
  effectiveDate?: Date | string;
  expirationDate?: Nullable<Date | string>;
  isActive?: boolean;
  /** Criterio de orden: 'NAME', 'TYPE' o null (orden de carga). */
  cardSort?: Nullable<string>;
  /** Lista anterior, del mismo formato. `null` desvincula; omitido no la toca. */
  previousBanlistId?: Nullable<number>;
  /** Reemplaza TODAS las cartas. Omitir para no tocarlas. */
  cards?: BanlistCardInput[];
  /** Reemplaza TODOS los arquetipos. Omitir para no tocarlos. */
  archetypes?: BanlistArchetypeInput[];
  /** Reemplaza TODAS las genéricas. Omitir para no tocarlas. */
  generics?: BanlistGenericInput[];
  /** Reemplaza TODOS los patrones prohibidos. Omitir para no tocarlos. */
  patterns?: BanlistCardPatternInput[];
};

export const getBanlists = async (
  user: Nullable<UserWithToken>,
  filters: BanlistFilters = {}
) =>
  getGraphQLQueryClient(user).request<WithMeta<Banlist>>(
    gql`
      ${BANLIST_FIELDS}
      query banlists($filters: BanlistFilters) {
        data: banlists(filters: $filters) {
          data {
            ...BanlistFields
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    { filters }
  );

/** Las listas de los organizadores del usuario. */
export const getMyBanlists = async (
  user: Nullable<UserWithToken>,
  filters: BanlistFilters = {}
) =>
  getGraphQLQueryClient(user).request<WithMeta<Banlist>>(
    gql`
      ${BANLIST_FIELDS}
      query myBanlists($filters: BanlistFilters) {
        data: myBanlists(filters: $filters) {
          data {
            ...BanlistFields
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    { filters }
  );

/**
 * Lo mínimo para poblar un selector: nombre, formato y dueño.
 *
 * `getMyBanlists` arrastra todas las cartas y arquetipos de cada lista, que
 * para elegir una en un combo no se miran nunca y son cientos de filas por
 * organizador.
 */
export type BanlistOption = Pick<
  Banlist,
  | 'id'
  | 'name'
  | 'formatId'
  | 'isActive'
  | 'organizerId'
  | 'logoUrl'
  | 'effectiveDate'
>;

/** Las listas de los organizadores del usuario, sin cartas ni arquetipos. */
export const getMyBanlistOptions = async (
  user: Nullable<UserWithToken>,
  filters: BanlistFilters = {}
) =>
  getGraphQLQueryClient(user).request<WithMeta<BanlistOption>>(
    gql`
      query myBanlistOptions($filters: BanlistFilters) {
        data: myBanlists(filters: $filters) {
          data {
            id
            name
            formatId
            isActive
            organizerId
            logoUrl
            effectiveDate
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    { filters }
  );

/**
 * Una opción con su dueño, para las pantallas que listan las de todos.
 *
 * El nombre del organizador es lo que distingue a dos listas homónimas, así que
 * en un selector que mezcla dueños no alcanza con `organizerId`.
 */
export type BanlistOptionWithOwner = BanlistOption & {
  organizer: Nullable<{ id: number; name: string }>;
  /** Los formatos que la tienen como lista fija (ver `globalBanlistAt`). */
  formats: Pick<Banlist['formats'][number], 'id'>[];
};

/**
 * Las listas visibles para el selector, sin cartas ni arquetipos.
 *
 * Es `getBanlists` recortada: aquélla trae `cards`, `archetypes`, `generics` y
 * `patterns` de CADA lista, y con `limit: 0` sobre un formato como Advanced
 * —más de setenta listas de cientos de filas— eso es un payload enorme para
 * dibujar un combo donde no se mira ninguna carta.
 */
export const getBanlistOptions = async (
  user: Nullable<UserWithToken>,
  filters: BanlistFilters = {}
) =>
  getGraphQLQueryClient(user).request<WithMeta<BanlistOptionWithOwner>>(
    gql`
      query banlistOptions($filters: BanlistFilters) {
        data: banlists(filters: $filters) {
          data {
            id
            name
            formatId
            isActive
            organizerId
            logoUrl
            effectiveDate
            organizer {
              id
              name
            }
            formats {
              id
            }
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    { filters }
  );

export const getBanlist = async (user: Nullable<UserWithToken>, id: number) =>
  getGraphQLQueryClient(user).request<WithData<Banlist>>(
    gql`
      ${BANLIST_FIELDS}
      query banlist($id: Int!) {
        data: banlist(id: $id) {
          ...BanlistFields
          metaDecks {
            tier
            metaDeck {
              id
              name
              description
              archetype {
                id
                name
                type
              }
              engines {
                id
                name
                type
              }
            }
          }
        }
      }
    `,
    { id }
  );

/**
 * La lista vigente de un formato. Sin `organizerId` devuelve la global (la de
 * Konami), que es lo que veían todos los consumidores antes de que las listas
 * tuvieran dueño.
 */
export const getActiveBanlist = async (
  user: Nullable<UserWithToken>,
  formatId: number,
  organizerId?: Nullable<number>
) =>
  getGraphQLQueryClient(user).request<WithData<Banlist>>(
    gql`
      ${BANLIST_FIELDS}
      query activeBanlist($formatId: Int!, $organizerId: Int) {
        data: activeBanlist(formatId: $formatId, organizerId: $organizerId) {
          ...BanlistFields
        }
      }
    `,
    { formatId, organizerId }
  );

export const createBanlist = async (
  user: Nullable<UserWithToken>,
  input: CreateBanlistInput
) =>
  getGraphQLQueryClient(user).request<WithData<Banlist>>(
    gql`
      ${BANLIST_FIELDS}
      mutation createBanlist($input: CreateBanlistInput!) {
        data: createBanlist(input: $input) {
          ...BanlistFields
        }
      }
    `,
    { input }
  );

export const updateBanlist = async (
  user: Nullable<UserWithToken>,
  id: number,
  input: UpdateBanlistInput
) =>
  getGraphQLQueryClient(user).request<WithData<Banlist>>(
    gql`
      ${BANLIST_FIELDS}
      mutation updateBanlist($id: Int!, $input: UpdateBanlistInput!) {
        data: updateBanlist(id: $id, input: $input) {
          ...BanlistFields
        }
      }
    `,
    { id, input }
  );

export const deleteBanlist = async (user: Nullable<UserWithToken>, id: number) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation deleteBanlist($id: Int!) {
        data: deleteBanlist(id: $id)
      }
    `,
    { id }
  );

export const setActiveBanlist = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<Banlist>>(
    gql`
      ${BANLIST_FIELDS}
      mutation setActiveBanlist($id: Int!) {
        data: setActiveBanlist(id: $id) {
          ...BanlistFields
        }
      }
    `,
    { id }
  );

/**
 * La lista vigente de un formato, sin sesión.
 *
 * Existe además de `getActiveBanlist` porque esa query pide sesión: el
 * middleware sólo deja pasar lo que está en su allowlist o empieza con
 * `public`, y `activeBanlist` no es ninguna de las dos. Devuelve `null` cuando
 * no hay lista vigente, en vez de tirar.
 */
export const getPublicActiveBanlist = async (
  formatId: number,
  organizerId?: Nullable<number>
) =>
  getGraphQLQueryClient(null).request<WithData<Nullable<Banlist>>>(
    gql`
      ${BANLIST_FIELDS}
      query publicActiveBanlist($formatId: Int!, $organizerId: Int) {
        data: publicActiveBanlist(
          formatId: $formatId
          organizerId: $organizerId
        ) {
          ...BanlistFields
        }
      }
    `,
    { formatId, organizerId: organizerId ?? null }
  );

/**
 * Las banlists vigentes de un organizador, sin sesión.
 *
 * Toma `null` como usuario a propósito: la lee un jugador desde el link que le
 * pasaron, y ahí no hay cuenta.
 */
export const getPublicBanlists = async (organizerSlug: string) =>
  getGraphQLQueryClient(null).request<WithData<Banlist[]>>(
    gql`
      ${BANLIST_FIELDS}
      query publicBanlists($organizerSlug: String!) {
        data: publicBanlists(organizerSlug: $organizerSlug) {
          ...BanlistFields
        }
      }
    `,
    { organizerSlug }
  );

/** Una lista puntual por su hash de compartir, sin sesión. */
export const getPublicBanlist = async (shareHash: string) =>
  getGraphQLQueryClient(null).request<WithData<Banlist>>(
    gql`
      ${BANLIST_FIELDS}
      query publicBanlist($shareHash: String!) {
        data: publicBanlist(shareHash: $shareHash) {
          ...BanlistFields
        }
      }
    `,
    { shareHash }
  );

/**
 * Qué cambió de una lista a la otra.
 *
 * Sin `againstId` se compara contra la anterior del mismo (formato, dueño);
 * con él, contra cualquier otra lista visible del mismo formato —que es como
 * se mira "qué le agrega mi lista a la de Konami".
 *
 * Toma el usuario porque el mismo diff lo miran el jugador (sin cuenta), el
 * organizador y el admin: la query es pública, y la sesión sólo amplía qué
 * listas se pueden elegir como base.
 */
export const getBanlistDiff = async (
  user: Nullable<UserWithToken>,
  id: number,
  againstId?: Nullable<number>
) =>
  getGraphQLQueryClient(user).request<WithData<Nullable<BanlistDiff>>>(
    gql`
      query publicBanlistDiff($id: Int!, $againstId: Int) {
        data: publicBanlistDiff(id: $id, againstId: $againstId) {
          banlist {
            id
            name
            effectiveDate
            organizerName
          }
          base {
            id
            name
            effectiveDate
            organizerName
          }
          cards {
            konamiId
            name
            image
            frameType
            from
            to
            movement
          }
          archetypesAdded {
            archetypeId
            name
            representativeKonamiId
            representativeImage
          }
          archetypesRemoved {
            archetypeId
            name
            representativeKonamiId
            representativeImage
          }
          options {
            id
            name
            effectiveDate
            organizerName
          }
        }
      }
    `,
    { id, againstId: againstId ?? null }
  );

/**
 * El histórico: sólo los encabezados, para llenar un selector.
 *
 * Sin `formatIds` trae los de todos los formatos, y sin `organizerId` tanto
 * las globales como las de organizadores. Ninguno de los dos se normaliza con
 * `?? null` como en `getPublicActiveBanlist`: acá "no dije nada" y "quiero las
 * globales" son cosas distintas, y mandar `null` filtraría.
 */
export const getPublicBanlistHistory = async (
  formatIds?: number[],
  organizerId?: Nullable<number>
) =>
  getGraphQLQueryClient(null).request<WithData<BanlistDiffRef[]>>(
    gql`
      query publicBanlistHistory($formatIds: [Int!], $organizerId: Int) {
        data: publicBanlistHistory(
          formatIds: $formatIds
          organizerId: $organizerId
        ) {
          id
          name
          formatId
          effectiveDate
          organizerName
        }
      }
    `,
    {
      ...(formatIds ? { formatIds } : {}),
      ...(organizerId === undefined ? {} : { organizerId }),
    }
  );

/**
 * Una lista puntual por id, sin sesión.
 *
 * Existe además de `getPublicBanlist` (por hash) porque el selector de
 * histórico devuelve ids: una lista vieja de Konami nunca se compartió, no
 * tiene hash, y por hash no hay forma de llegar. `null` = no existe o no se
 * puede ver; el API no los distingue a propósito.
 */
export const getPublicBanlistById = async (id: number) =>
  getGraphQLQueryClient(null).request<WithData<Nullable<Banlist>>>(
    gql`
      ${BANLIST_FIELDS}
      query publicBanlistById($id: Int!) {
        data: publicBanlistById(id: $id) {
          ...BanlistFields
        }
      }
    `,
    { id }
  );

/** El hash del link público, generándolo la primera vez. Idempotente. */
export const shareBanlist = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<string>>(
    gql`
      mutation shareBanlist($id: Int!) {
        data: shareBanlist(id: $id)
      }
    `,
    { id }
  );

/** El .xlsx de la lista en base64. Sólo el dueño o un admin. */
export const getBanlistExport = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<string>>(
    gql`
      query banlistExport($id: Int!) {
        data: banlistExport(id: $id)
      }
    `,
    { id }
  );

/**
 * El logo de la lista. Va por REST porque es multipart y `/graphql` sólo
 * acepta JSON; el mismo camino que el logo de un organizador.
 *
 * La lista tiene que existir: el logo se sube contra su id, así que en el alta
 * recién se puede después de guardar.
 */
export const uploadBanlistLogo = (
  banlistId: number,
  file: File,
  config?: AxiosRequestConfig
) => {
  const formData = new FormData();
  formData.append('logo', file);

  return axiosClient.post(`/banlists/${banlistId}/logo`, formData, {
    ...config,
    headers: {
      ...config?.headers,
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const deleteBanlistLogo = (
  banlistId: number,
  config?: AxiosRequestConfig
) => axiosClient.delete(`/banlists/${banlistId}/logo`, config);
